import { useAuth } from '../../auth/AuthContext';
import { LimitationEngine } from '../../utils/limitations';

interface PremiumBadgeProps {
  feature?: string;
  scale?: string;
  inline?: boolean;
  className?: string;
}

export function PremiumBadge({ feature, scale, inline = false, className = '' }: PremiumBadgeProps) {
  const { user } = useAuth();
  const userTier = user?.tier || 'free';

  // Don't show badge for premium users
  if (userTier === 'premium') {
    return null;
  }

  // Check if this specific feature/scale is premium-only
  let isPremiumOnly = false;
  
  if (scale) {
    isPremiumOnly = !LimitationEngine.checkScaleAccess(scale as any, userTier);
  } else if (feature) {
    isPremiumOnly = !LimitationEngine.checkFeatureAccess(feature, userTier);
  }

  if (!isPremiumOnly) {
    return null;
  }

  const badgeClass = inline 
    ? `premium-badge-inline ${className}` 
    : `premium-badge ${className}`;

  return (
    <span className={badgeClass} title="Premium Feature">
      💎
    </span>
  );
}

// Add styles via CSS-in-JS for now, should be moved to a CSS file
const styles = `
.premium-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  color: var(--color-neon-blue);
  font-size: 0.8rem;
  z-index: 10;
  text-shadow: 0 0 6px var(--color-neon-blue);
  background: none;
  border: none;
  padding: 0;
}

.premium-badge-inline {
  color: var(--color-neon-blue);
  font-size: 0.7rem;
  margin-left: 4px;
  text-shadow: 0 0 4px var(--color-neon-blue);
  background: none;
  border: none;
  padding: 0;
}

/* Add disabled state for premium-only buttons */
.scale-button.premium-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  position: relative;
}

.scale-button.premium-disabled:hover {
  background-color: rgba(6, 26, 68, 0.8) !important;
  box-shadow: 0 0 6px rgba(0, 221, 255, 0.2) !important;
  transform: none !important;
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('premium-badge-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'premium-badge-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
